// Every user-visible string of the syllabus PDF, resolved ONCE by the caller.
//
// Why the labels come in as data instead of the document calling `t()` itself:
// `SyllabusPdfDocument` is rendered through `pdf(<Doc/>)` from
// `@react-pdf/renderer`, which mounts it in its own renderer OUTSIDE the React
// tree that carries the i18next provider. A `useTranslation()` inside the
// document is therefore not reliable. Resolving in the calling component — a
// normal React component with the provider above it — and passing the result
// down is what makes the document translatable at all.
//
// Consequence worth stating: this file is the ONLY place the PDF's vocabulary
// lives, so the two callers (the template editor's preview and the portal
// download) cannot label the same document differently.
//
// Terminology note: the keys reuse the wording the student-facing screen
// already ships (`portal.syllabus.*`), because that is the most-read surface.
// Before this, the PDF said "Sección" where the screen said "Bloque",
// "Penalización por atraso" where the screen said "Política de atraso", and
// "Cronograma de entregas" where the screen said "Calendario de entregas".

import type { TFunction } from 'i18next';
import { inLocale } from './i18n-format';

export interface SyllabusPdfLatePolicy {
  firstWindowHours: number;
  firstWindowPenalty: number;
  secondWindowHours: number;
  secondWindowPenalty: number;
  dailyPenaltyAfter: number;
}

export interface SyllabusPdfLabels {
  // Cover + running header
  coverTagline: string;
  instructorToBeAssigned: string;

  // Section headings
  framework: string;
  mission: string;
  department: string;
  departmentObjectives: string;
  program: string;
  programObjectives: string;
  courseDescription: string;
  courseObjectives: string;
  philosophy: string;
  assessment: string;
  grades: string;
  rubrics: string;
  requiredTexts: string;
  complementaryBibliography: string;
  sessionFormat: string;
  scheduleByModule: string;
  deadlines: string;
  courseCalendar: string;
  colCourseWork: string;
  pagesShort: (pages: number) => string;
  policies: string;
  latePolicyHeading: string;
  anchorVersePrefix: string;

  // Table headers
  colComponent: string;
  colWeight: string;
  colCriterion: string;
  colReading: string;
  colPages: string;
  colBlock: string;
  colSchedule: string;
  colDate: string;
  colTopic: string;
  colKeyTexts: string;
  colWeek: string;
  colSubmission: string;

  // Empty / invalid states
  invalidSchedule: string;
  noSessions: string;
  noModules: string;

  // Default copy when the instructor authored none
  defaultPoliciesNote: string;
  defaultSessionFormatNote: string;

  // Formatters — the document has values at render time that only the active
  // language knows how to phrase.
  blockNumbered: (n: number) => string;
  breakNumbered: (n: number) => string;
  breakLabeled: (label: string) => string;
  minutes: (n: number) => string;
  readingsPlanHeading: (pages: number) => string;
  passingGrade: (grade: number) => string;
  workload: string;
  assignmentDetails: string;
  distribution: string;
  objectiveShort: (n: number) => string;
  colTask: string;
  colHours: string;
  total: string;
  latePolicyParagraph: (p: SyllabusPdfLatePolicy) => string;
  textbookFallback: (id: string) => string;
  pagesSuffix: (pages: number) => string;
  termHeading: (startIso: string, endIso: string) => string;
  termRange: (startIso: string, endIso: string) => string;
  weekday: (weekdayKey: string) => string;

  // Editor-preview only: that surface has no real Term, so it labels rows by
  // week number instead of by date.
  weekShort: (n: number) => string;
  weekWithDay: (n: number, weekdayKey: string) => string;
  previewButton: string;
  tenantFallback: string;
}

function parseIso(iso: string): Date | null {
  if (!iso || iso.includes('YYYY')) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function buildSyllabusPdfLabels(t: TFunction, lang: string): SyllabusPdfLabels {
  // Month names come from the platform rather than a hardcoded table: the old
  // `SPANISH_MONTHS` array made every date in the document Spanish no matter
  // the language, and Intl already knows every locale we ship.
  const monthName = (d: Date): string => inLocale(lang).date(d, { month: 'long', timeZone: 'UTC' });

  return {
    coverTagline: t('syllabusPdf.coverTagline'),
    instructorToBeAssigned: t('portal.syllabus.instructorToBeAssigned'),

    framework: t('syllabusPdf.framework'),
    mission: t('syllabusPdf.mission'),
    department: t('syllabusPdf.department'),
    departmentObjectives: t('syllabusPdf.departmentObjectives'),
    program: t('syllabusPdf.program'),
    programObjectives: t('syllabusPdf.programObjectives'),
    courseDescription: t('syllabusPdf.courseDescription'),
    courseObjectives: t('syllabusPdf.courseObjectives'),
    philosophy: t('syllabusPdf.philosophy'),
    assessment: t('syllabusPdf.assessment'),
    grades: t('syllabusPdf.grades'),
    rubrics: t('syllabusPdf.rubrics'),
    requiredTexts: t('syllabusPdf.requiredTexts'),
    complementaryBibliography: t('syllabusPdf.complementaryBibliography'),
    sessionFormat: t('syllabusPdf.sessionFormat'),
    scheduleByModule: t('syllabusPdf.scheduleByModule'),
    deadlines: t('syllabusPdf.deadlines'),
    courseCalendar: t('syllabusPdf.courseCalendar'),
    colCourseWork: t('syllabusPdf.col.courseWork'),
    pagesShort: (pages) => t('syllabusPdf.pagesShort', { pages }),
    policies: t('syllabusPdf.policies'),
    latePolicyHeading: t('syllabusPdf.latePolicyHeading'),
    anchorVersePrefix: t('syllabusPdf.anchorVersePrefix'),

    colComponent: t('syllabusPdf.col.component'),
    colWeight: t('syllabusPdf.col.weight'),
    colCriterion: t('syllabusPdf.col.criterion'),
    colReading: t('syllabusPdf.col.reading'),
    colPages: t('syllabusPdf.col.pages'),
    colBlock: t('syllabusPdf.col.block'),
    colSchedule: t('syllabusPdf.col.schedule'),
    colDate: t('syllabusPdf.col.date'),
    colTopic: t('syllabusPdf.col.topic'),
    colKeyTexts: t('syllabusPdf.col.keyTexts'),
    colWeek: t('syllabusPdf.col.week'),
    colSubmission: t('syllabusPdf.col.submission'),

    invalidSchedule: t('syllabusPdf.invalidSchedule'),
    noSessions: t('syllabusPdf.noSessions'),
    noModules: t('syllabusPdf.noModules'),

    defaultPoliciesNote: t('syllabusPdf.defaultPoliciesNote'),
    defaultSessionFormatNote: t('syllabusPdf.defaultSessionFormatNote'),

    blockNumbered: (n) => t('syllabusPdf.blockNumbered', { n }),
    breakNumbered: (n) => t('syllabusPdf.breakNumbered', { n }),
    breakLabeled: (label) => t('syllabusPdf.breakLabeled', { label }),
    minutes: (n) => t('syllabusPdf.minutes', { n }),
    readingsPlanHeading: (pages) => t('syllabusPdf.readingsPlanHeading', { pages }),
    passingGrade: (grade) => t('syllabusPdf.passingGrade', { grade }),
    workload: t('syllabusPdf.workload'),
    assignmentDetails: t('syllabusPdf.assignmentDetails'),
    distribution: t('syllabusPdf.distribution'),
    objectiveShort: (n) => t('syllabusPdf.objectiveShort', { n }),
    colTask: t('syllabusPdf.col.task'),
    colHours: t('syllabusPdf.col.hours'),
    total: t('syllabusPdf.total'),
    latePolicyParagraph: (p) => t('syllabusPdf.latePolicyParagraph', { ...p }),
    textbookFallback: (id) => t('syllabusPdf.textbookFallback', { id }),
    pagesSuffix: (pages) => t('syllabusPdf.pagesSuffix', { pages }),

    termHeading: (startIso, endIso) => {
      const a = parseIso(startIso);
      const b = parseIso(endIso);
      if (!a || !b) return `${startIso} — ${endIso}`;
      const startMonth = monthName(a);
      const endMonth = monthName(b);
      const year = b.getUTCFullYear();
      if (a.getUTCFullYear() === b.getUTCFullYear() && startMonth === endMonth) {
        return `${capitalize(startMonth)} ${year}`;
      }
      return `${capitalize(startMonth)} – ${capitalize(endMonth)} ${year}`;
    },

    // The whole sentence is a key, not a concatenation: the connectors and the
    // day/month order differ per language ("3 de marzo al 12 de junio de 2026"
    // vs "March 3 – June 12, 2026"), so only the translation can decide the shape.
    termRange: (startIso, endIso) => {
      const a = parseIso(startIso);
      const b = parseIso(endIso);
      if (!a || !b) return '';
      return t('syllabusPdf.termRange', {
        startDay: a.getUTCDate(),
        startMonth: monthName(a),
        endDay: b.getUTCDate(),
        endMonth: monthName(b),
        year: b.getUTCFullYear(),
      });
    },

    weekday: (weekdayKey) => t(`portal.syllabus.weekdays.${weekdayKey}`),

    weekShort: (n) => t('syllabusPdf.weekShort', { n }),
    weekWithDay: (n, weekdayKey) =>
      t('syllabusPdf.weekWithDay', { n, weekday: t(`portal.syllabus.weekdays.${weekdayKey}`) }),
    previewButton: t('syllabusPdf.previewButton'),
    tenantFallback: t('syllabusPdf.tenantFallback'),
  };
}
